import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Wallet, Building2, Phone, MapPin, Briefcase, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { MetricsCard } from '../components/common/MetricsCard';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { AnalyticsChart } from '../components/common/AnalyticsChart';
import { TransactionTable } from '../components/common/TransactionTable';
import { Loader } from '../components/common/Loader';
import { getChartData, getTransactionCount, getTransactionSummary, getCategoryBreakdown } from '../services/analytics';
import { getTransactions } from '../services/transactions';
import { getCategories } from '../services/categories';
import { checkCompanyProfile } from '../services/auth';
import { useAuth } from '../hooks/useAuth';

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [pieChartData, setPieChartData] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    industry: '',
    address: '',
    phone: '',
    business_type: 'product',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [userFirstName, setUserFirstName] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch categories first
      let categoriesList = [];
      try {
        const catData = await getCategories();
        categoriesList = Array.isArray(catData) ? catData : (catData.categories || catData.results || []);
      } catch (err) {
      }
      
      let totalTransactionCount = 0;
      let summaryData = null;
      let chartDataFromApi = null;
      
      // Check company profile and get transaction count, summary, and chart data
      if (user && user.id) {
        const token = localStorage.getItem('token');
        const profileCheck = await checkCompanyProfile(user.id, token);
        
        if (profileCheck.success) {
          // Extract and set first name from full_name
          if (profileCheck.full_name) {
            const firstName = profileCheck.full_name.split(' ')[0];
            setUserFirstName(firstName);
          }
          
          // Store company_id if available
          if (profileCheck.company_id) {
            localStorage.setItem('company_id', profileCheck.company_id);
            
            // Fetch transaction count for this company
            try {
              const countData = await getTransactionCount(profileCheck.company_id);
              totalTransactionCount = countData.total_transactions || 0;
            } catch (err) {
              totalTransactionCount = 0;
            }
            
            // Fetch transaction summary for this company
            try {
              const summaryRawData = await getTransactionSummary(profileCheck.company_id);
              summaryData = {
                totalIncome: summaryRawData.income || summaryRawData.total_income || 0,
                totalExpenses: summaryRawData.expenses || summaryRawData.total_expense || 0,
                netProfit: summaryRawData.profit_loss || summaryRawData.net || 0,
              };
            } catch (err) {
              summaryData = null;
            }
            
            // Fetch chart data for this company
            try {
              chartDataFromApi = await getChartData(profileCheck.company_id);
            } catch (err) {
              chartDataFromApi = null;
            }
            
            // Fetch category breakdown for this company
            try {
              const categoryData = await getCategoryBreakdown(profileCheck.company_id);
              setPieChartData({
                labels: categoryData.labels,
                values: categoryData.values,
              });
            } catch (err) {
              setPieChartData(null);
            }
          }
          
          if (!profileCheck.has_company_profile) {
            // Show company profile form if user doesn't have one
            setShowCompanyForm(true);
          } else {
            // Save company profile info to localStorage
            localStorage.setItem('companyProfile', JSON.stringify({
              business_type: profileCheck.business_type
            }));
          }
        }
      }
      
      // Use API chart data
      const chart = chartDataFromApi || {
        labels: [],
        datasets: [],
      };
      
      // Update stats with actual data from API - don't use mock data fallbacks
      setStats({
        totalIncome: summaryData?.totalIncome ?? 0,
        totalExpenses: summaryData?.totalExpenses ?? 0,
        netProfit: summaryData?.netProfit ?? 0,
        transactionsThisMonth: totalTransactionCount,
      });
      setChartData(chart);
      
      // Fetch recent transactions from API
      try {
        const data = await getTransactions();
        let transactionList = Array.isArray(data) ? data : (data.transactions || data.results || []);
        
        // Enrich transactions with category names if category is just an ID
        transactionList = transactionList.map(tx => {
          if (tx.category && !isNaN(tx.category) && categoriesList.length > 0) {
            const foundCategory = categoriesList.find(cat => cat.id === tx.category || cat.id.toString() === tx.category);
            return { ...tx, category: foundCategory?.name || tx.category };
          }
          return tx;
        });
        
        // Get the latest 5 transactions
        setRecentTransactions(transactionList.slice(0, 5));
      } catch (err) {
        setRecentTransactions([]);
      }
    };
    fetchData();
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleViewTransaction = (transactionId) => {
    navigate(`/transactions/${transactionId}/view`);
  };

  const handleEditTransaction = (transactionId) => {
    navigate(`/transactions/${transactionId}/edit`);
  };

  const handleDeleteTransaction = (transactionId) => {
    setTransactionToDelete(transactionId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTransaction = () => {
    if (!transactionToDelete) return;
    setRecentTransactions(recentTransactions.filter(tx => tx.id !== transactionToDelete));
    setShowDeleteConfirm(false);
    setTransactionToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.business_name) newErrors.business_name = 'Business name is required';
    if (!formData.industry) newErrors.industry = 'Industry is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.phone) newErrors.phone = 'Phone number is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem('token');
      const userId = user?.id;

      if (!userId) {
        setErrors({ submit: 'User ID not found. Please log in again.' });
        setLoading(false);
        return;
      }

      const profileData = {
        ...formData,
        created_by: userId,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/v1'}/company/profile/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (response.ok) {
        // Save to localStorage for sidebar
        localStorage.setItem('companyProfile', JSON.stringify({
          ...formData,
          business_type: formData.business_type,
        }));
        setSuccess(true);
        // Redirect to categories page after profile creation
        setTimeout(() => navigate('/categories/add'), 1500);
      } else {
        setErrors({ submit: data.message || data.detail || 'Failed to create company profile' });
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to create company profile' });
    } finally {
      setLoading(false);
    }
  };

  if (showCompanyForm) {
    return (
      <AuthenticatedLayout>
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
          <div className="w-full max-w-4xl px-4 sm:px-6">
            {/* Form Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 sm:p-16">
              {/* Icon */}
              <div className="flex justify-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-105 transition">
                  <Building2 className="text-white w-8 h-8" />
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-10">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">Complete Your Profile</h1>
                <p className="text-gray-600 dark:text-gray-400 mt-3 font-medium text-lg">Tell us about your business to get started</p>
              </div>

              {/* Success Message */}
              {success && (
                <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 rounded-lg flex items-center gap-3 text-green-700 dark:text-green-400 text-sm font-medium">
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                  <span>Company profile created successfully!</span>
                </div>
              )}

              {/* Error Message */}
              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg flex items-center gap-3 text-red-700 dark:text-red-400 text-sm font-medium">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span>{errors.submit}</span>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Business Name Field */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Business Name</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="business_name"
                      placeholder="Your company name"
                      value={formData.business_name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                        errors.business_name ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.business_name && <p className="text-red-600 text-xs mt-1">{errors.business_name}</p>}
                </div>

                {/* Industry Field */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Industry</label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="industry"
                      placeholder="e.g., Technology, Retail"
                      value={formData.industry}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                        errors.industry ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.industry && <p className="text-red-600 text-xs mt-1">{errors.industry}</p>}
                </div>

                {/* Address Field */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="address"
                      placeholder="Your business address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                        errors.address ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}
                </div>

                {/* Phone Field */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                        errors.phone ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
                </div>

                {/* Business Type Field */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Business Type</label>
                  <select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition bg-white dark:bg-gray-700 dark:text-white"
                  >
                    <option value="product">Product</option>
                    <option value="service">Service</option>
                  </select>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold text-base hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors shadow-md mt-10"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating Profile...
                    </span>
                  ) : (
                    'Complete Setup'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="space-y-6 sm:space-y-8">
        {/* Page Header */}
        <div className="px-4 sm:px-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {(() => {
              const hour = new Date().getHours();
              const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
              const displayName = userFirstName || (user?.email ? user.email.split('@')[0]?.replace(/[._-]/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) : 'there') || 'there';
              return `${greeting}, ${displayName}`;
            })()}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 sm:mt-2">Here's your financial overview for today.</p>
        </div>

        {/* Metrics Cards */}
        {stats === null ? (
          <Loader message="Loading Dashboard..." size="lg" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 px-4 sm:px-0">
            <MetricsCard
              label="Total Income"
              value={`₦${stats.totalIncome.toLocaleString()}`}
              icon={TrendingUp}
              trend={12}
            />
            <MetricsCard
              label="Total Expenses"
              value={`₦${stats.totalExpenses.toLocaleString()}`}
              icon={TrendingDown}
              trend={-5}
            />
            <MetricsCard
              label={stats.netProfit < 0 ? 'Net Loss' : 'Net Profit'}
              value={`₦${Math.abs(stats.netProfit).toLocaleString()}`}
              icon={Wallet}
              trend={stats.netProfit < 0 ? -18 : 18}
              isNegative={stats.netProfit < 0}
            />
            <MetricsCard
              label="Transactions"
              value={stats.transactionsThisMonth}
              icon={BarChart3}
            />
          </div>
        )}

        {/* Charts */}
        {stats !== null && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 px-4 sm:px-0">
            <AnalyticsChart title="Income vs Expenses" type="bar" data={chartData} />
            <AnalyticsChart title="Category Breakdown" type="pie" data={pieChartData} />
          </div>
        )}

        {/* Recent Transactions */}
        {stats !== null && (
        <div className="px-0 sm:px-0">
          <Card className="px-4 sm:px-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Transactions</h2>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full px-4 sm:px-0">
                <TransactionTable
                  transactions={recentTransactions}
                  onView={handleViewTransaction}
                  onEdit={handleEditTransaction}
                  onDelete={handleDeleteTransaction}
                />
              </div>
            </div>
          </Card>
        </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="max-w-md">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Transaction</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to delete this transaction? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <Button 
                  variant="danger" 
                  onClick={confirmDeleteTransaction}
                  className="flex-1"
                >
                  Delete
                </Button>
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setTransactionToDelete(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
};
